/**
 * Section 17 — Unified Communication & Activity Timeline (admin surface).
 *
 * Read-only React component that fetches /api/admin/timeline and renders a
 * chronological list. Accepts any one of the four scope props — pass the one
 * that matches the parent context (appointment / customer / booking / trial
 * lead). Filters are self-contained. Melbourne-formatted timestamps.
 *
 * Test-ids on every interactive element for the testing agent.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Mail, MessageSquare, RotateCcw, XCircle, AlertTriangle,
  CheckCircle2, Clock, RefreshCw, Send, Flag, Undo2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_CHIPS = [
  { key: "any",    label: "All" },
  { key: "email",  label: "Email" },
  { key: "sms",    label: "SMS" },
  { key: "action", label: "Actions" },
];
const EVENT_CHIPS = [
  { key: "any",          label: "Any" },
  { key: "reschedule",   label: "Reschedule" },
  { key: "cancellation", label: "Cancellation" },
  // Section 8 — Trial closure outcomes surface as `closure` events.
  { key: "closure",      label: "Closure" },
];
const STATUS_CHIPS = [
  { key: "any",       label: "All" },
  { key: "delivered", label: "Delivered" },
  { key: "sent",      label: "Sent" },
  { key: "failed",    label: "Failed" },
  { key: "queued",    label: "Queued" },
  { key: "retrying",  label: "Retrying" },
];

const STATUS_STYLE = {
  queued:    "bg-slate-100 text-slate-700 border-slate-300",
  sending:   "bg-sky-50 text-sky-700 border-sky-200",
  sent:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-300",
  failed:    "bg-red-50 text-red-700 border-red-200",
  retrying:  "bg-amber-50 text-amber-700 border-amber-200",
};

function fmtMelbourne(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Melbourne", dateStyle: "medium", timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function Timeline({
  appointmentId = "",
  customerId    = "",
  bookingId     = "",
  trialLeadId   = "",
  title         = "Communication Timeline",
  defaultLimit  = 100,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({ action: 0, email: 0, sms: 0 });
  const [channel, setChannel] = useState("any");
  const [event, setEvent] = useState("any");
  const [status, setStatus] = useState("any");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const scope = useMemo(() => {
    const q = new URLSearchParams();
    if (appointmentId) q.set("appointment_id", appointmentId);
    if (customerId)    q.set("customer_id",    customerId);
    if (bookingId)     q.set("booking_id",     bookingId);
    if (trialLeadId)   q.set("trial_lead_id",  trialLeadId);
    return q;
  }, [appointmentId, customerId, bookingId, trialLeadId]);

  const fetchTimeline = useCallback(async () => {
    if (!scope.toString()) return;
    setLoading(true);
    setError("");
    const q = new URLSearchParams(scope);
    q.set("channel", channel);
    q.set("event", event);
    q.set("status", status);
    if (dateFrom) q.set("from", dateFrom);
    if (dateTo)   q.set("to",   dateTo);
    q.set("limit", String(defaultLimit));
    try {
      const token = localStorage.getItem("adminToken") || localStorage.getItem("mahra_admin_token") || "";
      const res = await fetch(`${API}/admin/timeline?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setEntries(data.entries || []);
      setCounts(data.counts || { action: 0, email: 0, sms: 0 });
    } catch (e) {
      setError(e?.message || String(e));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [scope, channel, event, status, dateFrom, dateTo, defaultLimit]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  const totalScoped = useMemo(() =>
    (counts.action || 0) + (counts.email || 0) + (counts.sms || 0),
    [counts],
  );

  if (!scope.toString()) return null;

  return (
    <section className="border border-[#282828]/10 bg-white" data-testid="timeline-root">
      <header className="px-4 py-3 border-b border-[#282828]/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-[13px] uppercase tracking-[0.14em] text-[#3D2B1F]">{title}</h3>
          <span className="text-[11px] text-[#676664]/60" data-testid="timeline-total-count">{totalScoped} entries</span>
        </div>
        <button
          onClick={fetchTimeline}
          disabled={loading}
          className="text-[11px] uppercase tracking-[0.12em] text-[#9A7B4F] hover:text-[#6E5636] disabled:opacity-50 flex items-center gap-1"
          data-testid="timeline-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {/* Filters */}
      <div className="p-3 border-b border-[#282828]/10 space-y-2" data-testid="timeline-filters">
        <FilterRow label="Channel"  chips={CHANNEL_CHIPS} value={channel} onChange={setChannel} idPrefix="channel" />
        <FilterRow label="Event"    chips={EVENT_CHIPS}   value={event}   onChange={setEvent}   idPrefix="event" />
        <FilterRow label="Status"   chips={STATUS_CHIPS}  value={status}  onChange={setStatus}  idPrefix="status" />
        <div className="flex items-center gap-2 pt-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/70 w-16">Range</span>
          <input
            type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="border border-[#282828]/12 px-2 py-1 text-[11px]" data-testid="timeline-date-from"
          />
          <span className="text-[11px] text-[#676664]/40">→</span>
          <input
            type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="border border-[#282828]/12 px-2 py-1 text-[11px]" data-testid="timeline-date-to"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="text-[10px] uppercase tracking-[0.12em] text-[#676664] hover:text-[#282828]"
              data-testid="timeline-date-clear"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading && entries.length === 0 && (
        <div className="p-6 flex items-center gap-2 text-[12px] text-[#676664]/60" data-testid="timeline-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading timeline…
        </div>
      )}
      {error && (
        <div className="p-4 text-[11px] text-red-700 bg-red-50 border-t border-red-200" data-testid="timeline-error">
          {error}
        </div>
      )}
      {!loading && !error && entries.length === 0 && (
        <div className="p-6 text-[12px] text-[#676664]/60 text-center" data-testid="timeline-empty">
          No events match these filters.
        </div>
      )}
      {entries.length > 0 && (
        <ol className="divide-y divide-[#282828]/8" data-testid="timeline-list">
          {entries.map((e) => (
            <TimelineRow key={e.id} entry={e} />
          ))}
        </ol>
      )}
    </section>
  );
}

function FilterRow({ label, chips, value, onChange, idPrefix }) {
  return (
    <div className="flex items-center flex-wrap gap-1">
      <span className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/70 w-16">{label}</span>
      {chips.map((c) => (
        <button
          key={c.key}
          onClick={() => onChange(c.key)}
          data-testid={`timeline-filter-${idPrefix}-${c.key}`}
          className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] border transition-colors ${
            value === c.key
              ? "bg-[#9A7B4F] text-white border-[#9A7B4F]"
              : "bg-white text-[#676664] border-[#282828]/12 hover:border-[#9A7B4F]/40"
          }`}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

function TimelineRow({ entry }) {
  const meta = describeEntry(entry);
  return (
    <li className="px-4 py-3 flex gap-3" data-testid={`timeline-entry-${entry.id}`}>
      <div className="flex-shrink-0 w-6 pt-0.5">{meta.icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center flex-wrap gap-2 text-[12px] text-[#282828]">
          <span className="font-medium">{meta.title}</span>
          {entry.status && (
            <span
              className={`text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 border ${STATUS_STYLE[entry.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}
              data-testid={`timeline-status-badge-${entry.id}`}
            >
              {entry.status}
            </span>
          )}
          <span className="text-[10px] text-[#676664]/60" data-testid={`timeline-timestamp-${entry.id}`}>
            {fmtMelbourne(entry.created_at)}
          </span>
        </div>
        {meta.sub && <div className="text-[11px] text-[#676664]/80 mt-0.5">{meta.sub}</div>}
        {meta.recipient && <div className="text-[10px] text-[#676664]/60 mt-0.5">→ {meta.recipient}</div>}
        {entry.error_message && (
          <div className="text-[10px] text-red-600 mt-0.5" data-testid={`timeline-error-${entry.id}`}>
            {entry.error_message}
          </div>
        )}
        {entry.provider_message_id && (
          <div className="text-[9px] font-mono text-[#676664]/50 mt-0.5">msg id: {entry.provider_message_id}</div>
        )}
      </div>
    </li>
  );
}

function describeEntry(e) {
  // Actions
  if (e.kind === "action" && e.action === "trial_rescheduled") {
    return {
      icon: <RotateCcw className="w-4 h-4 text-amber-600" />,
      title: "Trial rescheduled",
      sub: `${e.old_date} ${e.old_time} → ${e.new_date} ${e.new_time}`,
      recipient: `Initiated by ${e.initiated_by || "system"}`,
    };
  }
  if (e.kind === "action" && e.action === "trial_cancelled") {
    const reason = e.cancellation_reason
      ? (e.cancellation_reason === "other" && e.cancellation_reason_other)
        ? `Other — ${e.cancellation_reason_other}`
        : humanReason(e.cancellation_reason)
      : "";
    return {
      icon: <XCircle className="w-4 h-4 text-red-600" />,
      title: "Trial cancelled",
      sub: reason ? `Reason: ${reason}` : "Reason: Not provided",
      recipient: `Initiated by ${e.initiated_by || "system"}`,
    };
  }
  // Section 8 — Trial closure outcomes
  if (e.kind === "action" && (e.action === "trial_closed" || e.action === "trial_outcome_updated")) {
    const outcomeLabel = humanOutcome(e.outcome);
    const parts = [];
    if (e.action === "trial_outcome_updated" && e.previous_outcome) {
      parts.push(`${humanOutcome(e.previous_outcome)} → ${outcomeLabel}`);
    } else {
      parts.push(outcomeLabel);
    }
    if (e.outcome_notes) parts.push(`"${e.outcome_notes}"`);
    if (e.linked_booking_id) parts.push(`Booking: ${e.linked_booking_id}`);
    return {
      icon: <Flag className={`w-4 h-4 ${e.outcome === "converted" ? "text-emerald-600" : "text-slate-600"}`} />,
      title: e.action === "trial_outcome_updated" ? "Trial outcome updated" : "Trial closed",
      sub: parts.join(" · "),
      recipient: `Initiated by ${e.initiated_by || "admin"}${e.actor ? ` (${e.actor})` : ""}`,
    };
  }
  if (e.kind === "action" && e.action === "trial_reopened") {
    return {
      icon: <Undo2 className="w-4 h-4 text-indigo-600" />,
      title: "Trial reopened",
      sub: e.reopen_reason ? `Reason: ${e.reopen_reason}` : `Restored to ${(e.new_status || "confirmed").replace("_", " ")}`,
      recipient: `Initiated by ${e.initiated_by || "admin"}${e.actor ? ` (${e.actor})` : ""}`,
    };
  }
  // Comms
  const isAdmin = e.initiated_by === "admin" || (e.recipient_email || "").endsWith("@shopmahra.com.au");
  if (e.kind === "email") {
    const icon = e.status === "failed"
      ? <AlertTriangle className="w-4 h-4 text-red-600" />
      : e.status === "delivered"
      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
      : e.status === "queued" || e.status === "retrying"
      ? <Clock className="w-4 h-4 text-amber-600" />
      : <Mail className="w-4 h-4 text-slate-600" />;
    return {
      icon,
      title: `${isAdmin ? "Admin" : "Customer"} email · ${e.subject || e.template_name || e.email_type || "Untitled"}`,
      sub: e.body_preview,
      recipient: [e.recipient_name, e.recipient_email].filter(Boolean).join(" — "),
    };
  }
  if (e.kind === "sms") {
    const icon = e.status === "failed"
      ? <AlertTriangle className="w-4 h-4 text-red-600" />
      : <MessageSquare className="w-4 h-4 text-slate-600" />;
    return {
      icon,
      title: `${isAdmin ? "Admin" : "Customer"} SMS · ${e.template_name || e.sms_type || "Message"}`,
      sub: e.body_preview,
      recipient: [e.recipient_name, e.recipient_phone].filter(Boolean).join(" — "),
    };
  }
  return { icon: <Send className="w-4 h-4 text-slate-400" />, title: e.action || e.kind, sub: "", recipient: "" };
}

const REASON_LABELS = {
  change_of_plans:      "Change of plans",
  illness:              "Illness",
  rebooking:            "Rebooking for another date",
  found_another_outfit: "Found another outfit",
  no_longer_attending:  "No longer attending the event",
  other:                "Other",
  prefer_not_to_say:    "Prefer not to say",
  customer_requested:   "Not provided",
};
function humanReason(code) {
  return REASON_LABELS[(code || "").toLowerCase()] || code;
}

// Section 8 — Trial closure outcome labels for the Timeline row.
const OUTCOME_LABELS = {
  converted:          "Converted",
  follow_up:          "Follow up",
  did_not_convert:    "Did not convert",
  no_show:            "No-show",
  customer_cancelled: "Customer cancelled",
  admin_cancelled:    "Admin cancelled",
  rescheduled:        "Rescheduled",
  could_not_contact:  "Could not contact",
  duplicate:          "Duplicate",
  other:              "Other",
};
function humanOutcome(code) {
  return OUTCOME_LABELS[(code || "").toLowerCase()] || code || "—";
}

export default Timeline;
